import { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import { updateBasicDetails } from "../../Services/MyProfile";
import { uploadaudio } from "../../Services/MyProfile";
import { convertVoicetoName } from "../../Services/arisAI";
import { uploadProfilePhoto, generateUploadURL } from "../../Services/auth";
import { ReactComponent as Infocircle } from "../../Assets/icon/info-circle.svg";
import { ReactComponent as Check } from "../../Assets/icon/check.svg";
import placeholder from "../../Assets/images/placeholder.png";
import { ReactComponent as Calender } from "../../Assets/icon/calendar.svg";
import { ReactComponent as CloseIcon } from "../../Assets/icon/close.svg";
import { IoIosArrowDown } from "react-icons/io";
// import { Loader2 } from "lucide-react";
import "./style.css";
import { useHeader } from "../../Providers/HeaderContextProvider";
// Voice Preference moved to Settings page
import CameraCaptureModal from "../CameraCaptureModal";

const NAME_REGEX = /^[A-Za-z0-9\s'-]+$/;

const ProfileModal = ({ onClose, profileData, onProfileUpdate, id }) => {
  const { addProfileData, profileContent } = useHeader();

  const [formData, setFormData] = useState({
    first_name: profileData.first_name || "",
    last_name: profileData.last_name || "",
    date_of_birth: profileData.DOB
      ? formatDateForDisplay(new Date(profileData.DOB))
      : "",
    gender: profileData.gender || "",
    originalAudio: null,
    responseAudio: null,
    profileImage: null,
  });

  const [birthday, setBirthday] = useState(
    profileData.DOB ? new Date(profileData.DOB) : null
  );
  const [imagePreview, setImagePreview] = useState(
    profileData.profile_pic_url || null
  );
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [isEdited, setIsEdited] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isImageModified, setIsImageModified] = useState(false);
  const [isFieldsModified, setIsFieldsModified] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const originalAudioRef = useRef(new Audio());
  const responseAudioRef = useRef(new Audio());
  const datePickerRef = useRef(null);
  const initialProfileData = useRef(profileData);
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const [hasExistingAudio, setHasExistingAudio] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    first_name: "",
    last_name: "",
  });
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [currentAudioType, setCurrentAudioType] = useState(null);

  // Function to format date to mm-dd-yyyy
  function formatDateForDisplay(date) {
    return date
      ? `${(date.getMonth() + 1).toString().padStart(2, "0")}-${date
          .getDate()
          .toString()
          .padStart(2, "0")}-${date.getFullYear()}`
      : "";
  }

  useEffect(() => {
    // Check if there's existing audio from the API
    if (profileData.name_audio_link) {
      setHasExistingAudio(true);
      setFormData((prev) => ({
        ...prev,
        responseAudio: profileData.name_audio_link,
      }));
    } else {
      setHasExistingAudio(false);
    }
  }, [profileData.name_audio_link]);

  useEffect(() => {
    // Clean up audio when component unmounts
    return () => {
      originalAudioRef.current.pause();
      responseAudioRef.current.pause();
    };
  }, []);

  const handleAudioPlay = (audioType) => {
    const audioRef =
      audioType === "original"
        ? originalAudioRef.current
        : responseAudioRef.current;
    const otherAudioRef =
      audioType === "original"
        ? responseAudioRef.current
        : originalAudioRef.current;
    const audioUrl =
      audioType === "original"
        ? formData.originalAudio
        : hasExistingAudio
        ? profileData.name_audio_link
        : formData.responseAudio;

    if (!audioUrl) {
      setShowErrorMessage(true);
      setStatusMessage(
        `${
          audioType === "original" ? "Original" : "Processed"
        } audio is not available`
      );
      setTimeout(() => setShowErrorMessage(false), 3000);
      return;
    }

    try {
      if (isPlaying && currentAudioType === audioType) {
        audioRef.pause();
        setIsPlaying(false);
        setCurrentAudioType(null);
      } else {
        otherAudioRef.pause();
        audioRef.src = audioUrl;

        audioRef.onerror = () => {
          setShowErrorMessage(true);
          setStatusMessage(
            `Error playing ${
              audioType === "original" ? "original" : "processed"
            } audio`
          );
          setTimeout(() => setShowErrorMessage(false), 3000);
          setIsPlaying(false);
          setCurrentAudioType(null);
        };

        audioRef.play().catch((error) => {
          console.error("Error playing audio:", error);
          setShowErrorMessage(true);
          setStatusMessage(`Error playing audio`);
          setTimeout(() => setShowErrorMessage(false), 3000);
          setIsPlaying(false);
          setCurrentAudioType(null);
        });

        setIsPlaying(true);
        setCurrentAudioType(audioType);
      }

      audioRef.onended = () => {
        setIsPlaying(false);
        setCurrentAudioType(null);
      };
    } catch (error) {
      console.error("Error handling audio playback:", error);
      setShowErrorMessage(true);
      setStatusMessage(`Error playing audio`);
      setTimeout(() => setShowErrorMessage(false), 3000);
    }
  };

  const handleEditAudio = () => {
    setFormData((prev) => ({
      ...prev,
      originalAudio: null,
      responseAudio: null,
    }));
    setHasExistingAudio(false);
    setIsEdited(true);
    setIsFieldsModified(true);
  };

  const handleRecordingComplete = async (blob, url) => {
    try {
      setFormData((prev) => ({
        ...prev,
        originalAudio: url,
      }));

      setIsProcessingAudio(true);

      const responseBlob = await convertVoicetoName(blob); // returns Blob if success
      const responseUrl = URL.createObjectURL(responseBlob); // create object URL

      setFormData((prev) => ({
        ...prev,
        responseAudio: responseUrl,
      }));

      setIsEdited(true);
      setIsFieldsModified(true);
    } catch (error) {
      console.error("Error converting voice:", error);
      setShowErrorMessage(true);

      // Priority order for error messages
      if (error.detail) {
        setStatusMessage(error.detail);
      } else if (error.response?.data?.detail) {
        setStatusMessage(error.response.data.detail);
      } else if (error.message) {
        setStatusMessage(error.message);
      } else {
        setStatusMessage("Error processing audio. Please try recording again.");
      }

      setFormData((prev) => ({
        ...prev,
        originalAudio: null,
        responseAudio: null,
      }));

      // Show error for exactly 3 seconds
      setTimeout(() => setShowErrorMessage(false), 3000);
    } finally {
      setIsProcessingAudio(false);
    }
  };

  const handleSubmit = async () => {
    if (isImageModified && !isFieldsModified) {
      onClose();
      return;
    }

    if (!isEdited) {
      onClose();
      return;
    }

    setLoading(true);
    setStatusMessage("");

    try {
      // Upload both original and response audio if they exist
      if (formData.originalAudio && formData.responseAudio) {
        const originalResponse = await fetch(formData.originalAudio);
        const responseResponse = await fetch(formData.responseAudio);

        const originalBlob = await originalResponse.blob();
        const responseBlob = await responseResponse.blob();

        const originalFile = new File([originalBlob], "original.mp3", {
          type: "audio/mpeg",
        });
        const responseFile = new File([responseBlob], "response.mp3", {
          type: "audio/mpeg",
        });

        // Upload both audio files
        await uploadaudio({
          originalAudio: originalFile,
          responseAudio: responseFile,
        });
      }

      if (isFieldsModified) {
        const basicDetails = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          date_of_birth: birthday
            ? formatDateForDisplay(birthday)
            : formData.date_of_birth,
          gender: formData.gender,
          name_audio_link: formData.responseAudio, // Use response audio as the pronunciation
        };

        await updateBasicDetails(basicDetails);

        onProfileUpdate({
          ...profileData,
          ...basicDetails,
          profile_pic_url: imagePreview,
        });
      }
      onClose();
    } catch (error) {
      console.error("Error updating profile:", error);
      setShowErrorMessage(true);
      setStatusMessage("Error updating profile");
      setTimeout(() => setShowErrorMessage(false), 2000);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    setFormData({
      first_name: profileData.first_name || "",
      last_name: profileData.last_name || "",
      date_of_birth: profileData.DOB
        ? formatDateForDisplay(new Date(profileData.DOB))
        : "",
      gender: profileData.gender || "",
      pronunciation: profileData.name_audio_link || "",
      profileImage: null,
    });

    setBirthday(profileData.DOB ? new Date(profileData.DOB) : null);
    setImagePreview(profileData.profile_pic_url || null);
    initialProfileData.current = profileData;
  }, [profileData]);

const handleInputChange = (field, value) => {
  const errors = { ...validationErrors };

  // Get the current values to check combined length
  const currentFirstName = field === "first_name" ? value : formData.first_name;
  const currentLastName = field === "last_name" ? value : formData.last_name;
  const combinedLength = (currentFirstName || "").length + (currentLastName || "").length;

  if (field === "first_name") {
    if (!value) {
      errors.first_name = "First name is required";
    } else if (combinedLength > 50) {
      errors.first_name = "First name and last name together cannot exceed 50 characters";
    } else if (value !== value?.trim()) {
      errors.first_name = "Name should not begin or end with spaces";
    } else if (!NAME_REGEX.test(value)) {
      errors.first_name = "Name should only contain letters, numbers, spaces, hyphens, and apostrophes";
    } else {
      errors.first_name = "";
    }
  }

  if (field === "last_name") {
    if (value) {
      if (combinedLength > 50) {
        errors.last_name = "First name and last name together cannot exceed 50 characters";
      } else if (value !== value?.trim()) {
        errors.last_name = "Name should not begin or end with spaces";
      } else if (!NAME_REGEX.test(value)) {
        errors.last_name = "Name should only contain letters, numbers, spaces, hyphens, and apostrophes";
      } else {
        errors.last_name = "";
      }
    } else {
      errors.last_name = "";
    }
  }

  setValidationErrors(errors);

  if (field === "date_of_birth" && value) {
    value = new Date(value);
  }

  setFormData((prev) => {
    const updatedData = { ...prev, [field]: value };
    const hasFieldChanges = !isEqual(updatedData, initialProfileData.current);
    setIsFieldsModified(hasFieldChanges);
    setIsEdited(hasFieldChanges || isImageModified);
    return updatedData;
  });
};

  const hasValidationErrors = () => {
    return Object.values(validationErrors).some((error) => error !== "");
  };

const isFormValid = () => {
  return (
    formData.first_name !== "" &&
    formData.last_name !== "" &&
    !hasValidationErrors() &&
    isEdited
  );
};


  const handleDateChange = (date) => {
    if (date) {
      // Ensure the date is at the start of the day to avoid timezone issues
      const adjustedDate = new Date(
        Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
      );

      const formattedDate = formatDateForDisplay(adjustedDate);

      handleInputChange("date_of_birth", formattedDate);
      setBirthday(adjustedDate);
    } else {
      handleInputChange("date_of_birth", "");
      setBirthday(null);
    }
  };

const handleFileChange = async (file) => {
  if (!file) return;

  const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
  if (!allowedTypes.includes(file.type)) {
    setShowErrorMessage(true);
    setStatusMessage("Only JPEG, JPG, and PNG files are allowed.");
    setTimeout(() => setShowErrorMessage(false), 5000);
    return;
  }

  try {
    setUploadingImage(true);

    const response = await generateUploadURL(file.name, file.type);
    if (!response?.upload_url) throw new Error("No upload URL received");

    const requestOptions = {
      method: "PUT",
      headers: {
        "Content-Type": file.type,
      },
      body: file,
    };

    const uploadResponse = await fetch(response.upload_url, requestOptions);
    if (uploadResponse.status !== 200) throw new Error("Upload to S3 failed");

    const uploadResult = await uploadProfilePhoto(response.file_key);
    const imageUrl = uploadResult?.payload?.download_url;

    if (!imageUrl) throw new Error("Server did not return uploaded image URL");

    // ✅ Success: Update state and show success message
    setImagePreview(imageUrl);
    localStorage.setItem(
      "profile",
      JSON.stringify({
        ...profileContent,
        profile_pic_url: imageUrl,
      })
    );
    addProfileData({
      ...profileContent,
      profile_pic_url: imageUrl,
    });

    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 5000);

  } catch (error) {
  
    console.error("Image upload failed:", error);
    setShowErrorMessage(true);
    setStatusMessage("Failed to upload image. Please try again later.");
    setTimeout(() => setShowErrorMessage(false), 5000);
  } finally {
    setUploadingImage(false);
  }
};


  const openDatePicker = () => {
    if (datePickerRef.current) {
      datePickerRef.current.setFocus(); // Programmatically focus/open the DatePicker
    }
  };
  const isEqual = (obj1, obj2) => JSON.stringify(obj1) === JSON.stringify(obj2);

  // Voice Preference removed from this modal

  return (
    <div className="uploadImage-modal">
      <div className="uploadImage-modalContent">
        <button className="uploadImage-closeButton" onClick={onClose}>
          <CloseIcon />
        </button>
        <div className="uploadImage-header">
          <div className="uploadImage-avatarWrapper">
            <img
              src={imagePreview || placeholder}
              alt="Profile"
              className="uploadImage-avatar"
              onClick={() =>
                document.getElementById("profileImageInput").click()
              }
            />
            <div className="basic-profile-pic-btn">
              <label className="basic-input-lable">Add a Photo of You: </label>
              <label
                htmlFor="profileImageInput"
                className="basic-upload-button"
              >
                {uploadingImage ? "Uploading..." : "Upload"}
              </label>
              <input
                type="file"
                id="profileImageInput"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => handleFileChange(e.target.files[0])}
                disabled={uploadingImage}
              />
              <label className="basic-input-lable">or</label>
              <button
                type="button"
                className="basic-take-photo"
                onClick={() => setIsCameraOpen(true)}
                disabled={uploadingImage}
              >
                {uploadingImage ? "Uploading..." : "Take photo"}
              </button>
            </div>
          </div>
        </div>
        <CameraCaptureModal
          isOpen={isCameraOpen}
          onClose={() => setIsCameraOpen(false)}
          loading={uploadingImage}
          onCapture={async (file) => {
            // Show local preview immediately (match Signup behavior)
            try {
              const reader = new FileReader();
              reader.onloadend = () => setImagePreview(reader.result);
              reader.readAsDataURL(file);
            } catch (e) {
              // no-op
            }
            await handleFileChange(file);
            setIsCameraOpen(false);
          }}
        />
        <div className="uploadImage-formGroup">
          <label>First Name</label>
          <input
            type="text"
            value={formData.first_name}
            onChange={(e) => handleInputChange("first_name", e.target.value)}
            className="uploadImage-input"
          />
          {validationErrors.first_name && (
            <p className="error-message name-align-left">
              {validationErrors.first_name}
            </p>
          )}
        </div>
        <div className="uploadImage-formGroup">
          <label>Last Name</label>
          <input
            type="text"
            value={formData.last_name}
            onChange={(e) => handleInputChange("last_name", e.target.value)}
            className="uploadImage-input"
          />
          {validationErrors.last_name && (
            <p className="error-message name-align-left">
              {validationErrors.last_name}
            </p>
          )}
        </div>

        <div>
          {/* <div className="name-pronunciation-container">
            <label>Name Pronunciation (optional)</label>
            <label className="say-my-name">Say "My name is (your name)"</label>

            {hasExistingAudio || formData.originalAudio ? (
              <div className="audio-controls-container">
                {isProcessingAudio ? (
                  <div className="audio-control">
                    <button className="update-play-button" disabled>
                      <Loader2 className="animate-spin" size={20} />
                    </button>
                  </div>
                ) : hasExistingAudio || formData.responseAudio ? (
                  <div className="audio-control">
                    <div>
                      <button
                        onClick={() => handleAudioPlay("response")}
                        className="update-play-button"
                      >
                        {isPlaying && currentAudioType === "response" ? (
                          <Pause size={20} />
                        ) : (
                          <Play size={20} />
                        )}
                        Recording
                      </button>
                    </div>
                    <div>
                      <button
                        onClick={handleEditAudio}
                        className="edit-button upload-edit-btn"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <VoiceRecorder
                onRecordingComplete={handleRecordingComplete}
                showEditButton={true}
              />
            )}
          </div> */}

          <div className="uploadImage-formGroup">
            <label>Date of Birth</label>{" "}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                padding: "8px 12px",
                color: "var(--Base-Black, #000)",
                fontFamily: "Urbanist",
                fontSize: "16px",
                fontStyle: "normal",
                boxSizing: "border-box",
                borderRadius: "12px",
                border: "1px solid var(--gray-light-hover, #E4E4E7)",
                background: "var(--Base-White, #FFF)",
              }}
            >
              <DatePicker
                value={formData.date_of_birth}
                selected={birthday} // Bind to the Date object
                onChange={handleDateChange}
                dateFormat="MM-dd-yyyy"
                placeholderText="mm-dd-yyyy"
                className="date-picker-container-input-edit"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                maxDate={new Date()}
                ref={datePickerRef}
              />

              <div onClick={openDatePicker} style={{ cursor: "pointer" }}>
                <Calender />
              </div>
            </div>
          </div>
          <div className="uploadImage-formGroup">
            <label>Gender</label>
            <div style={{ position: "relative" }}>
              <select
                value={formData.gender}
                onChange={(e) => handleInputChange("gender", e.target.value)}
                className="uploadImage-input"
              >
                <option value="" disabled={true}>
                  Select Gender
                </option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="non-binary">Non-binary</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
                <option value="other">Other</option>
              </select>
              <IoIosArrowDown
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  color: "#344054",
                }}
              />
            </div>
          </div>
          {/* Voice Preference moved to Settings page */}
        </div>

        <div className="editpage-popup">
          {showSuccessMessage && (
            <div>
              <div className="editpage-popup-content">
                <Check />
                Your photo has been updated.
              </div>
            </div>
          )}

          {showErrorMessage && (
            <div>
              <div
                className="editpage-popup-content"
                style={{ background: "rgba(240, 68, 56, 1)", color: "#fff" }}
              >
                <Infocircle />
                {statusMessage || "Something went wrong."}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "center" }}>
          <button
            className={`uploadImage-updateButton ${
              isFormValid() ? "edited" : ""
            }`}
            onClick={handleSubmit}
            disabled={loading || uploadingImage || !isFormValid()}
          >
            {loading ? "Updating..." : "Update Profile"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
